import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import '@adyen/adyen-web/dist/adyen.css';
import classNames from 'classnames';
import { forwardRef, useEffect, useImperativeHandle, useCallback } from 'react';
import { LockMid } from '@zooplus/icons';
import { Label, Checkbox } from '@zooplus/ui-components';
import { RENDER_CONTAINER_ID } from '../AdyenCreditCardForm/AdyenCreditCardForm.static.js';
import CardExpiryDateField from '../CardExpiryDateField/CardExpiryDateField.js';
import CardNumberFormField from '../CardNumberFormField/CardNumberFormField.js';
import CreditCardContentIcon from '../CreditCardContentIcon/CreditCardContentIcon.js';
import CVVIcon from '../CVVIcon/CVVIcon.js';
import DetectedCardBrandsController from '../DetectedCardBrandsController/DetectedCardBrandsController.js';
import EncryptedFormField from '../EncryptedFormField/EncyrptedFormField.js';
import FormControlWrapper from '../FormControlWrapper/FormControlWrapper.js';
import Message from '../Message/Message.js';
import SupportedCardList from '../SupportedCardList/SupportedCardList.js';
import useAdyenSecureFields from '../../hooks/useAdyenSecureFields.js';
import useAdyenState from '../../hooks/useAdyenState.js';
import useIsMobile from '../../hooks/useIsMobile.js';
import { useTranslation } from '../../hooks/useTranslations.js';
import { useAppContext } from '../../providers/AppContextProvider.js';
import { Sizes } from '../../types-internal/index.js';
import { adyenBrandOfCreditCardBrand } from '../../types-internal/AdyenTypes.js';
import { exposeGlobally } from '../../utils/utils.js';
import styles from './AdyenCreditCardFormNew.module.scss.js';

const DISPLAYABLE_CARD_TYPES = [
    'american_express',
    'carte_bancaire',
    'diners_club',
    'maestro',
    'master_card',
    'visa',
];
/**
 * Need to have this cached function, otherwise if we have `onFatalError = () => {}`
 * a new function will be constructed every time which will cause the useEffects depending on it to rerender
 */
const DEFAULT_ON_FATAL_ERROR = () => { };
/**
 * The UI to add a new credit card, the security is handled by Adyen.
 * Relies on the hook 'useAdyenState' (to capture the data from Adyen and the unmanaged inputs) and 'useAdyenSecureFields' (to initialize the Adyen 'securedfields').
 *
 * Captured data can be retrieved with the globally-exposed 'computeAdyenCreditCardFormData'.
 */
const AdyenCreditCardFormNew = forwardRef(({ adyenEnvironment, clientKey, allowVaulting = false, onFatalError = DEFAULT_ON_FATAL_ERROR }, forwardedRef) => {
    const { t } = useTranslation();
    const { locale } = useAppContext();
    const isXSMobile = useIsMobile(Sizes.XS);
    const { detectedCardBrands, onChange, onFieldValid, onHolderNameChange, storeInVault, onStoreInVaultChange, focusedElement, onFocus, creditCardBrand, onBrand, onBinLookup, computeAdyenCreditCardFormData, errors, } = useAdyenState(onFatalError);
    const secureFields = useAdyenSecureFields({
        clientKey,
        locale,
        t,
        adyenEnvironment,
        onChange,
        onFocus,
        onFieldValid,
        onBrand,
        onBinLookup,
        onFatalError,
    }, DISPLAYABLE_CARD_TYPES);
    // Expose the captured state globally so that external code can get the Adyen encrypted data
    useEffect(() => exposeGlobally('computeAdyenCreditCardFormData', computeAdyenCreditCardFormData), [computeAdyenCreditCardFormData]);
    // Expose the computeAdyenCreditCardFormData method internally too
    useImperativeHandle(forwardedRef, () => ({
        computeAdyenCreditCardFormData,
    }));
    /* Handle the card brand selection in case of co-branded */
    const onDualBrandingSelected = useCallback((brand) => {
        const adyenBrand = adyenBrandOfCreditCardBrand[brand];
        if (secureFields && adyenBrand) {
            secureFields.dualBrandingChangeHandler(adyenBrand);
        }
    }, [secureFields]);
    const isAmex = creditCardBrand === 'american_express';
    const noCardBrandDetected = !creditCardBrand;
    const detectedBrandController = (jsx(DetectedCardBrandsController, { detectedCardBrands: detectedCardBrands, selectedBrand: creditCardBrand, onBrandSelected: onDualBrandingSelected, coBrandedMessageClassName: styles.coBrandedMessageContainer }));
    return (jsxs("div", { id: RENDER_CONTAINER_ID, className: styles.adyenCreditCardForm, "data-zta": "adyen-cc-form-container", children: [jsx("div", { className: styles.secureFormMessage, children: jsx(Message, { color: "grey", text: t('checkout.payment.secure'), icon: jsx(LockMid, {}) }) }), jsxs("div", { className: styles.ccNumberInputContainer, children: [jsx(Label, { children: t('checkout.payment.creditcard.card_number') }), jsxs("div", { className: styles.supportedCardsBar, children: [noCardBrandDetected && (
                            // When no card brand detected, list of all supported brands should be displayed above the form
                            jsx(SupportedCardList, { size: "s", cardBrands: DISPLAYABLE_CARD_TYPES })), !noCardBrandDetected && isXSMobile && (
                            // When card brand is detected, and we are on mobile screen size, the detected card brands controller
                            // should be displayed above the form, outside the card number input field, opposite with if we are
                            // on larger screen size (desktop), the detected card brands controller is displayed inside the card
                            // number input field.
                            jsx(Fragment, { children: detectedBrandController }))] })] }), jsx(CardNumberFormField
            // Render the detected brand controller inside the input field only when we are on large screen (not on
            // mobile or smaller screen size).
            , { 
                // Render the detected brand controller inside the input field only when we are on large screen (not on
                // mobile or smaller screen size).
                id: "encryptedCardNumber" // In the new design the icon stays inside on mobile as well
                , detectedBrandsController: noCardBrandDetected ? jsx(CreditCardContentIcon, {}) : !isXSMobile && detectedBrandController, errorMessage: errors.encryptedCardNumber, focused: focusedElement === 'encryptedCardNumber', hasInfoMessage: false, hasLabel: false }), jsx(FormControlWrapper, { label: t('checkout.payment.creditcard.holder_name'), labelFor: "holderName", errorMessage: errors.holderName, errorZta: "errorInputCardName", children: jsx("input", { onChange: onHolderNameChange, className: classNames('z-text-input', 'z-input', 'z-form', styles.holderName, errors.holderName !== null && 'z-text-input--error'), type: "text", name: "cc-name", autoComplete: "cc-name", id: "holderName", "data-zta": "inputCardName" }) }), jsx(CardExpiryDateField, { focusedElement: focusedElement, errorMessage: errors.encryptedExpiryDate }), jsx(EncryptedFormField, { className: styles.cvv, id: "encryptedSecurityCode", labelText: t('checkout.payment.creditcard.cvc.generic_code'), icon: jsx(CVVIcon, { isAmex: isAmex }), infoMessage: isAmex
                    ? t('checkout.payment.creditcard.cid.warning')
                    : t('checkout.payment.creditcard.cvc.warning'), errorMessage: errors.encryptedSecurityCode, focused: focusedElement === 'encryptedSecurityCode', "data-zta": "inputCardCode" }), allowVaulting && (jsx(FormControlWrapper, { children: jsx(Checkbox, { className: styles.vaultCheck, id: "vault-check", checked: storeInVault, onChange: onStoreInVaultChange, "data-zta": "storeCreditCard", children: t('checkout.payment.save.creditcard') }) }))] }));
});

export { AdyenCreditCardFormNew as default };
//# sourceMappingURL=AdyenCreditCardFormNew.js.map
